import { mapGetters, mapMutations } from "vuex";
import HlpLazyLoadModal from "@Helpers/functions/lazyLoadModal";
import MixinBillable from "@Mixins/billable";
import MixinSubscriptions from "@Mixins/subscriptions";

export default {
  mixins: [MixinBillable, MixinSubscriptions],

  computed: {
    ...mapGetters([
      "isTrialingAllowed",
      "trialBannerPromoClosedAt",
      "trialBannerReminderClosedAt",
      "trialModalPromoClosedAt",
      "trialModalReminderHardClosedAt",
      "trialModalReminderSoftClosedAt",
    ]),

    isTrialBannerPromoVisible: {
      get() {
        const allowed = this.isTrialingAllowed;
        const available = this.isPast24Hours(this.trialBannerPromoClosedAt);
        return allowed && available;
      },
      set(newValue) {
        sessionStorage.setItem("trialBannerPromoClosedAt", newValue);
        this.SET_TRIAL_CLOSED_AT_VALUES({
          state: "trialBannerPromoClosedAt",
          timestamp: newValue,
        });
      },
    },
    isTrialBannerReminderVisible: {
      get() {
        return this.trialBannerReminderClosedAt === null
          ? true
          : this.trialBannerReminderClosedAt === "true";
      },
      set(newValue) {
        sessionStorage.setItem("trialBannerReminderClosedAt", newValue);
        this.SET_TRIAL_CLOSED_AT_VALUES({
          state: "trialBannerReminderClosedAt",
          timestamp: newValue,
        });
      },
    },
    isTrialModalPromoVisible: {
      get() {
        const allowed = this.isTrialingAllowed;
        const available = this.isPast24Hours(this.trialModalPromoClosedAt);
        return allowed && available;
      },
      set(newValue) {
        localStorage.setItem("trialModalPromoClosedAt", newValue);
        this.SET_TRIAL_CLOSED_AT_VALUES({
          state: "trialModalPromoClosedAt",
          timestamp: newValue,
        });
      },
    },
    isTrialModalReminderHardVisible: {
      get() {
        return this.isAfterNow(this.trialModalReminderHardClosedAt);
      },
      set(newValue) {
        localStorage.setItem("trialModalReminderHardClosedAt", newValue);
        this.SET_TRIAL_CLOSED_AT_VALUES({
          state: "trialModalReminderHardClosedAt",
          timestamp: newValue,
        });
      },
    },
    isTrialModalReminderSoftVisible: {
      get() {
        return this.isAfterNow(this.trialModalReminderSoftClosedAt);
      },
      set(newValue) {
        localStorage.setItem("trialModalReminderSoftClosedAt", newValue);
        this.SET_TRIAL_CLOSED_AT_VALUES({
          state: "trialModalReminderSoftClosedAt",
          timestamp: newValue,
        });
      },
    },

    onTrialButNoPaymentMethod() {
      return (
        this.getUserCurrentSubscription?.isOnTrial &&
        !this.billableHasPaymentMethod
      );
    },

    shouldTrialPromoModalAppear() {
      if (this.isUserSubscribed) return false;
      return this.isTrialModalPromoVisible;
    },
    shouldTrialReminderModalAppear() {
      return (
        this.shouldTrialReminderHardModalAppear ||
        this.shouldTrialReminderSoftModalAppear
      );
    },
    shouldTrialReminderHardModalAppear() {
      const allowed = this.onTrialButNoPaymentMethod;
      const remaining = this.getUserCurrentSubscription?.remainingTrialDays;
      const visible = remaining <= 1;

      return this.isTrialModalReminderHardVisible && allowed && visible;
    },
    shouldTrialReminderSoftModalAppear() {
      const allowed = this.onTrialButNoPaymentMethod;
      const remaining = this.getUserCurrentSubscription?.remainingTrialDays;
      const visible = remaining > 1 && remaining <= 7;

      return this.isTrialModalReminderSoftVisible && allowed && visible;
    },

    trialDayCountdown() {
      return (
        moment
          .utc(this.getUserCurrentSubscription?.trial_ends_at)
          .local()
          .valueOf() - moment.now()
      );
    },
  },

  methods: {
    ...mapMutations(["SET_TRIAL_CLOSED_AT_VALUES"]),

    /**
     * Checks if 24 hours have passed since the specified timestamp.
     *
     * @param {number|string} timestamp The timestamp in milliseconds format.
     * @returns {boolean} True if at least 24 hours have passed, false otherwise.
     *
     * Invalid timestamps are considered expired (returning true).
     */
    isPast24Hours(timestamp) {
      const timestampNumber = parseInt(timestamp, 10);
      const momentObject = moment(timestampNumber);

      if (!momentObject.isValid()) return true;
      return moment().diff(momentObject, "hour") >= 24;
    },

    /**
     * Checks if a given timestamp is in the future or not.
     *
     * @param {string} timestamp A string representation of a timestamp.
     * @returns {boolean} True if the timestamp is in the future, false if it's in the past or invalid.
     *
     * Invalid timestamps are considered future dates (returning true).
     *
     */
    isAfterNow(timestamp) {
      const timestampNumber = parseInt(timestamp, 10);
      const momentObject = moment(timestampNumber);

      if (!momentObject.isValid()) return true;
      return moment().isAfter(moment(momentObject));
    },

    /**
     * Formats a dictionary of countdown values by adding leading zeros to any value less than 10.
     *
     * @param {Object} props A dictionary containing countdown values such as seconds, minutes, hours, etc.
     * @returns {Object} A new dictionary with leading zeros prepended to values less than 10.
     *
     * @example
     * ```javascript
     * const countdownProps = { seconds: 5, minutes: 2, hours: 1 };
     * const formattedProps = countdownAddLeadingZero(countdownProps);
     * console.log(formattedProps); // { seconds: '05', minutes: '02', hours: '1' }
     * ```
     */
    countdownAddLeadingZero(props) {
      const formattedProps = {};

      Object.entries(props).forEach(([key, value]) => {
        formattedProps[key] = value < 10 ? `0${value}` : String(value);
      });

      return formattedProps;
    },

    /**
     * Removes promotional and banner-related timestamps from session and local storage.
     * This code is intended to be temporary and should be removed after a fair amount of time.
     */
    removeOldTrialKeys() {
      sessionStorage.removeItem("ftBannerPromTs");
      sessionStorage.removeItem("ftBannerStatusTs");
      localStorage.removeItem("ftModalPromTs");
      localStorage.removeItem("ftModalStatusHardTs");
      localStorage.removeItem("ftModalStatusSoftTs");
    },

    showTrialPromoModal() {
      const modal = "Promotion";

      Bus.$emit("showModal", HlpLazyLoadModal(`freetrial/${modal}`));
    },

    showTrialReminderModal() {
      let modal;

      if (this.shouldTrialReminderHardModalAppear) {
        modal = "freetrial/StatusHard";
      } else if (this.shouldTrialReminderSoftModalAppear) {
        modal = "freetrial/StatusSoft";
      }

      Bus.$emit("showModal", HlpLazyLoadModal(modal), {}, {}, {}, true);
    },
  },

  created() {
    this.removeOldTrialKeys();
  },
};
